import React, { useState } from "react";
import { API_BASE_URL } from "@lib/config";
import { useLivePoll } from "../hooks/useLivePoll";
import { describeFundGuard, type FundGuardPayload } from "@lib/fundGuard";

/* Every page: a red banner when an IBKR account that is not on the fund list holds money, a grey
 * line when a registered fund other than Horizon (Meridian) holds money these pages do not show
 * (M3 Decision 7, CIO 2026-10-28, until the M7 fund switch), an amber notice when that could not be
 * verified (fund blend guard, 2026-09-16). Reads a cached guard, never the broker, every 5 minutes
 * while the tab is visible. */
const FUND_GUARD_POLL_MS = 5 * 60 * 1000;

// Each page mounts its own Layout, so the banner remounts on every navigation. Remember the last
// answer for this browser session so a red banner is on screen immediately, not after the first
// fetch returns (or five minutes later, if that fetch fails).
let lastGuard: FundGuardPayload | null = null;

const FundGuardBanner: React.FC = () => {
  const [guard, setGuard] = useState<FundGuardPayload | null>(lastGuard);

  useLivePoll(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/fund_guard`, { cache: "no-store" });
      if (!res.ok) return;
      const j = await res.json();
      if (j && typeof j === "object") {
        lastGuard = j;
        setGuard(j);
      }
    } catch {
      /* keep the last state: a transient failure must not clear a banner */
    }
  }, FUND_GUARD_POLL_MS);

  const notice = describeFundGuard(guard);
  if (!notice) return null;
  const red = notice.tone === "red";
  const grey = notice.tone === "grey";
  return (
    // Same outer width and gutters as the page containers, so the banner lines up with the content.
    <div className="mx-auto max-w-[1880px] px-3 pt-3 sm:px-4 xl:px-5">
      <div
        role={red ? "alert" : "status"}
        data-testid="fund-guard-banner"
        data-tone={notice.tone}
        className={`rounded-lg px-4 ${red ? "py-3 text-[13px] font-semibold" : "py-2 text-[12px]"}`}
        style={
          red
            ? { backgroundColor: "rgb(var(--c-down) / 0.14)", border: "1px solid rgb(var(--c-down) / 0.55)" }
            : grey
              ? { backgroundColor: "rgba(148, 163, 184, 0.08)", border: "1px solid rgba(148, 163, 184, 0.30)" }
              : { backgroundColor: "rgba(245, 158, 11, 0.10)", border: "1px solid rgba(245, 158, 11, 0.40)" }
        }
      >
        {notice.text}
      </div>
    </div>
  );
};

export default FundGuardBanner;
